import { Fragment, useMemo, useState } from "react";
import { Check, ChevronRight, Pencil, ShieldAlert, ShieldCheck, Workflow } from "lucide-react";
import { DsBadge, DsButton, DsIcon, DsSpinner } from "../../../design-system";
import { SCREEN_ACCESS_ACTION_ORDER, groupScreensForAccess } from "../../../rbac/catalog";
import {
  ACTION_SHORT_LABELS,
  DATA_ACCESS_LEVELS,
  SWITCHABLE_MODULE_KEYS,
  actionState,
  isDefaultSystemRole,
  isModuleGranted,
  isSystemRole,
  moduleStats,
} from "./roleAccessUtils";
import { modulesGrantedCount, roleRisks } from "./roleRisks";

const selectClass =
  "ds-input h-8 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-2 text-xs text-[hsl(var(--ds-fg))] outline-none focus:border-[hsl(var(--ds-primary))] focus:ring-2 focus:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed disabled:opacity-50";

function ActionCheck({ checked, dirty, disabled, onToggle, label }) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      className={`inline-grid h-[22px] w-[22px] place-items-center rounded-md border-[1.5px] p-0 text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed disabled:opacity-50 ${
        checked
          ? "border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-primary))]"
          : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
      } ${dirty ? "shadow-[0_0_0_3px_hsl(var(--ds-warning)/0.45)]" : ""}`}
    >
      <DsIcon icon={Check} size="sm" className={`h-3.5 w-3.5 ${checked ? "opacity-100" : "opacity-0"}`} />
    </button>
  );
}

function SidebarSwitch({ on, onChange }) {
  return (
    <label
      className={`flex shrink-0 items-center gap-2 text-xs text-[hsl(var(--ds-fg))] ${onChange ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}
    >
      Show in sidebar
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label="Show Request and Approvals in sidebar"
        disabled={!onChange}
        onClick={() => onChange?.(!on)}
        className={`relative inline-flex h-5 w-9 items-center rounded-full p-0.5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed ${
          on ? "bg-[hsl(var(--ds-primary))]" : "bg-[hsl(var(--ds-muted))] ring-1 ring-inset ring-[hsl(var(--ds-border))]"
        }`}
      >
        <span
          className={`h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${on ? "translate-x-4" : "translate-x-0"}`}
        />
      </button>
    </label>
  );
}

/** Parents first, then nested children (parentKey) recursively. */
function orderScreensWithNesting(screens = []) {
  const list = Array.isArray(screens) ? screens : [];
  const byKey = new Map(list.map((s) => [s.key, s]));
  const byParent = new Map();
  const roots = [];
  for (const s of list) {
    const pk = s?.parentKey ? String(s.parentKey) : "";
    if (!pk || !byKey.has(pk)) {
      roots.push(s);
      continue;
    }
    if (!byParent.has(pk)) byParent.set(pk, []);
    byParent.get(pk).push(s);
  }
  const out = [];
  const walk = (node, depth) => {
    out.push({ ...node, _nestDepth: depth });
    for (const child of byParent.get(node.key) || []) walk(child, depth + 1);
  };
  for (const r of roots) walk(r, 0);
  return out;
}

function dataScopeLabel(level) {
  const l = DATA_ACCESS_LEVELS.find((x) => x.value === level);
  if (level === "all") return "Entire company";
  if (level === "department") return "Selected departments";
  if (level === "own") return "Own record only";
  if (level === "team") return "Reporting team";
  if (level === "branch_location") return "Selected branches";
  return l?.label || "Not set";
}

export default function RolePermissionMatrix({
  role,
  roles = [],
  catalog,
  access,
  savedAccess,
  usesAccess,
  loading,
  editable = false,
  locked = false,
  dataAccess,
  onChangeRole,
  onToggleAction,
  onSidebarToggle,
  onEditRole,
  pendingCount = 0,
  onDiscard,
  onReviewSave,
}) {
  const [expandedKey, setExpandedKey] = useState(null);
  const defaultRole = isDefaultSystemRole(role);
  const system = isSystemRole(role);
  const risks = roleRisks(role, catalog, access);

  const rows = useMemo(
    () =>
      catalog
        .map((mod) => ({
          mod,
          stats: moduleStats(mod, access?.[mod.key]),
          on: isModuleGranted(mod, access?.[mod.key]),
          switchable: defaultRole && SWITCHABLE_MODULE_KEYS.has(mod.key),
        }))
        .filter((r) => r.on || r.switchable),
    [catalog, access, defaultRole]
  );
  const grantedMods = rows.length;

  const toggle = (key) =>
    setExpandedKey((prev) => (prev === key ? null : key));

  const isDirty = (modKey, screenKey, action) => {
    const was = !!savedAccess?.[modKey]?.screens?.[screenKey]?.[action];
    const is = !!access?.[modKey]?.screens?.[screenKey]?.[action];
    return was !== is;
  };

  const shell = (body) => (
    <section
      aria-label="Permission matrix"
      className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)]"
    >
      {body}
    </section>
  );

  if (!role) {
    return shell(
      <div className="flex flex-1 items-center justify-center p-8">
        <p className="ds-body text-[hsl(var(--ds-muted-fg))]">Select a role to see its permissions.</p>
      </div>
    );
  }

  if (!usesAccess) {
    return shell(
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]">
          <DsIcon icon={Workflow} size="md" />
        </span>
        <h4 className="ds-h4 text-[hsl(var(--ds-fg))]">Workflow assignee only</h4>
        <p className="ds-body max-w-md text-[hsl(var(--ds-muted-fg))]">
          Action Based roles have no screen or data permissions. Change the role type to Access Based to grant
          screens.
        </p>
      </div>
    );
  }

  const canEdit = editable && !locked && !system;

  return shell(
    <>
      <div className="flex shrink-0 flex-col gap-2 border-b border-[hsl(var(--ds-border))] px-4 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <select
            aria-label="Role"
            value={role.id}
            onChange={(e) => onChangeRole?.(e.target.value)}
            className={`${selectClass} h-10 min-w-[180px] font-semibold text-[hsl(var(--ds-fg))]`}
          >
            {(roles || []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.name}
              </option>
            ))}
          </select>
          <span className="min-w-0 flex-1 truncate text-sm text-[hsl(var(--ds-muted-fg))]">
            {role.description || (system ? "System role" : "Custom role")}
          </span>
          {!system && canEdit && onEditRole ? (
            <DsButton size="sm" variant="outline" onClick={() => onEditRole(role)}>
              <DsIcon icon={Pencil} size="sm" />
              Edit role
            </DsButton>
          ) : null}
          <div className="flex items-center gap-2 text-sm text-[hsl(var(--ds-fg))]">
            <span className="text-xs font-semibold text-[hsl(var(--ds-primary))]">Data scope</span>
            <span
              className={`${selectClass} inline-flex h-9 min-w-[160px] cursor-default items-center opacity-90`}
              title="Set when creating or editing the role"
              aria-label="Role data scope"
            >
              {dataScopeLabel(dataAccess?.level || "all")}
            </span>
          </div>
          {system || locked ? <DsBadge tone="neutral">Locked</DsBadge> : null}
        </div>
        <div
          className={`flex w-full items-start gap-2 rounded-[11px] px-3.5 py-2.5 text-[12px] leading-snug ${
            risks.length
              ? "border border-[hsl(var(--ds-warning)/0.35)] bg-[hsl(var(--ds-warning)/0.14)] text-[hsl(var(--ds-fg))]"
              : "border border-[hsl(var(--ds-success)/0.25)] bg-[hsl(var(--ds-success)/0.1)] text-[hsl(var(--ds-success))]"
          }`}
          role="status"
        >
          <DsIcon
            icon={risks.length ? ShieldAlert : ShieldCheck}
            size="sm"
            className={`mt-0.5 shrink-0 ${risks.length ? "text-[hsl(var(--ds-warning))]" : ""}`}
          />
          <span className="min-w-0">
            {risks.length ? risks.join(" ") : "No access risks in this role."}
          </span>
        </div>
      </div>

      <div
        className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-[hsl(var(--ds-border))] px-[18px] py-3"
        aria-label="Role modules"
      >
        <span className="mr-1 text-xs font-semibold text-[hsl(var(--ds-primary))]">Modules</span>
        {rows.map(({ mod }) => (
          <button
            key={mod.key}
            type="button"
            title={`Open ${mod.label}`}
            onClick={() => setExpandedKey(mod.key)}
            className="rounded-full bg-[hsl(var(--ds-primary)/0.12)] px-2.5 py-0.5 text-xs font-medium text-[hsl(var(--ds-primary))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
          >
            {mod.label}
          </button>
        ))}
        <span className="ml-auto text-xs text-[hsl(var(--ds-muted-fg))]">
          {grantedMods} module{grantedMods === 1 ? "" : "s"} on this role
        </span>
      </div>
      <p className="shrink-0 border-b border-[hsl(var(--ds-border))] px-[18px] py-2 text-xs text-[hsl(var(--ds-muted-fg))]">
        Only modules selected on the role are listed. Use Edit role to add or remove modules. Data scope is set on the
        role.
      </p>

      {loading ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <DsSpinner />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center p-8">
          <p className="ds-body text-center text-[hsl(var(--ds-muted-fg))]">
            No modules on this role yet. Edit the role and select modules first.
          </p>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto overscroll-contain">
          <table className="w-full min-w-[720px] border-collapse text-left">
            <thead className="sticky top-0 z-[1] bg-[hsl(var(--ds-surface))]">
              <tr className="border-b border-[hsl(var(--ds-border))]">
                <th
                  scope="col"
                  className="px-4 py-2.5 text-left text-[calc(0.875rem*1.15)] font-bold uppercase tracking-wide text-[hsl(var(--ds-primary))]"
                >
                  Module
                </th>
                {SCREEN_ACCESS_ACTION_ORDER.map((a) => (
                  <th
                    key={a}
                    scope="col"
                    className="w-[72px] px-1 py-2.5 text-center text-[calc(0.875rem*1.15)] font-bold uppercase tracking-wide text-[hsl(var(--ds-primary))]"
                  >
                    {ACTION_SHORT_LABELS[a]}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(({ mod, stats, switchable }) => {
                const open = expandedKey === mod.key;
                const moduleAcc = access?.[mod.key];
                return (
                  <Fragment key={mod.key}>
                    <tr className="border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)]">
                      <th scope="row" className="px-2 py-2 font-normal">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => toggle(mod.key)}
                            aria-expanded={open}
                            className="flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1 text-left hover:bg-[hsl(var(--ds-muted))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
                          >
                            <DsIcon
                              icon={ChevronRight}
                              size="sm"
                              className={`text-[hsl(var(--ds-muted-fg))] transition-transform ${open ? "rotate-90" : ""}`}
                            />
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-sm font-semibold text-[hsl(var(--ds-fg))]">
                                {mod.label}
                              </span>
                              <span className="block text-[11px] text-[hsl(var(--ds-muted-fg))]">
                                {stats.screensOn}/{stats.screenTotal} screens · {stats.granted}/{stats.total}
                              </span>
                            </span>
                          </button>
                          {switchable ? (
                            <SidebarSwitch on={moduleAcc?.enabled !== false} onChange={onSidebarToggle} />
                          ) : null}
                        </div>
                      </th>
                      {SCREEN_ACCESS_ACTION_ORDER.map((a) => (
                        <td key={a} className="px-1 py-2 text-center">
                          <span className="text-[10px] font-semibold tabular-nums text-[hsl(var(--ds-muted-fg))]">
                            {actionState(mod.screens, moduleAcc, a) === "na"
                              ? "—"
                              : actionState(mod.screens, moduleAcc, a) === "full"
                                ? "All"
                                : actionState(mod.screens, moduleAcc, a) === "partial"
                                  ? "Some"
                                  : "Off"}
                          </span>
                        </td>
                      ))}
                    </tr>
                    {open
                      ? groupScreensForAccess(mod.screens)
                          .filter((group) => group.label !== "Legacy")
                          .map((group, gi) => (
                            <Fragment key={`${mod.key}-${group.label || gi}`}>
                              {group.label ? (
                                <tr className="border-b border-[hsl(var(--ds-border))]">
                                  <td
                                    colSpan={SCREEN_ACCESS_ACTION_ORDER.length + 1}
                                    className="px-11 pb-1 pt-2 text-[calc(0.875rem*1.15)] font-bold uppercase tracking-wide text-[hsl(var(--ds-primary))]"
                                  >
                                    {group.label}
                                  </td>
                                </tr>
                              ) : null}
                              {orderScreensWithNesting(group.screens).map((screen) => {
                                const depth = screen._nestDepth || 0;
                                const pad = depth === 0 ? "pl-11" : depth === 1 ? "pl-16" : "pl-20";
                                return (
                                  <tr key={screen.key} className="border-b border-[hsl(var(--ds-border))]">
                                    <th
                                      scope="row"
                                      className={`py-1.5 pr-2 text-sm font-medium text-[hsl(var(--ds-fg))] ${pad}`}
                                      title={screen.description || undefined}
                                    >
                                      {depth > 0 ? (
                                        <span className="text-[hsl(var(--ds-muted-fg))]">↳ {screen.label}</span>
                                      ) : (
                                        screen.label
                                      )}
                                    </th>
                                    {SCREEN_ACCESS_ACTION_ORDER.map((a) => {
                                      const declared = (screen.actions || []).includes(a);
                                      if (!declared) {
                                        return (
                                          <td
                                            key={a}
                                            className="px-1 py-1.5 text-center text-[hsl(var(--ds-border))]"
                                          >
                                            —
                                          </td>
                                        );
                                      }
                                      const checked = moduleAcc?.screens?.[screen.key]?.[a] === true;
                                      return (
                                        <td key={a} className="px-1 py-1.5 text-center">
                                          <ActionCheck
                                            checked={checked}
                                            dirty={isDirty(mod.key, screen.key, a)}
                                            disabled={!canEdit}
                                            label={`${mod.label} ${screen.label} ${ACTION_SHORT_LABELS[a]}`}
                                            onToggle={() => {
                                              if (!canEdit || !onToggleAction) return;
                                              onToggleAction(mod, screen.key, a, !checked);
                                            }}
                                          />
                                        </td>
                                      );
                                    })}
                                  </tr>
                                );
                              })}
                            </Fragment>
                          ))
                      : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="shrink-0 space-y-1.5 border-t border-[hsl(var(--ds-border))] px-[18px] py-3">
        {pendingCount > 0 ? (
          <div className="flex flex-wrap items-center gap-3 rounded-[11px] bg-[hsl(var(--ds-primary)/0.12)] px-3.5 py-2.5 text-[hsl(var(--ds-fg))]">
            <span className="flex-1 text-sm font-medium text-[hsl(var(--ds-primary))]">
              {pendingCount} unsaved permission change{pendingCount > 1 ? "s" : ""}
            </span>
            <DsButton size="sm" variant="outline" onClick={onDiscard}>
              Discard
            </DsButton>
            <DsButton size="sm" onClick={onReviewSave}>
              Review and save
            </DsButton>
          </div>
        ) : null}
      </div>
    </>
  );
}
